import {
  AbridgedInvoiceMetadata,
  AccountGuid,
  getTilledPaymentErrorDetail,
  isTilledPaymentErrorDetail,
  nextGuid,
  PaymentMethod,
  PaymentMethodCardMetadata,
  PaymentMethodCardType,
  PaymentStatus,
  paymentConfirmationOutcomeUnknownMessage,
  ThisShouldNeverHappenError,
  TilledAchDebitReturnErrorCodes,
  TilledCardDeclineErrorCodes,
  TilledPaymentErrorDetail,
  usCentsToUsd,
} from '@breezy/shared'
import { datadogRum } from '@datadog/browser-rum'
import { useCallback, useState } from 'react'
import { trpc } from '../../../hooks/trpc'
import { TilledFormInfo } from '../../../hooks/useTilled'
import { useMessage } from '../../../utils/antd-utils'
import { CardInfo, TilledCardFundingType, TilledPaymentMethodRequest } from '../../../utils/tilledSdkTypes'
import { AchPaymentFormData } from '../components/AchPaymentForm/AchPaymentForm'
import { CreditCardPaymentFormData } from '../components/CreditCardPaymentForm/CreditCardPaymentForm'
import { useCreateTilledPaymentMethod } from './useCreateTilledPaymentMethod'

export type PaymentFormData<PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH> =
  PaymentMethodType extends PaymentMethod.ACH
    ? AchPaymentFormData
    : PaymentMethodType extends PaymentMethod.CARD
      ? CreditCardPaymentFormData
      : never

export const isAchPaymentFormData = (
  formData: PaymentFormData<PaymentMethod.CARD | PaymentMethod.ACH>,
): formData is AchPaymentFormData => 'accountType' in formData

export const isCreditCardFormData = (
  formData: PaymentFormData<PaymentMethod.CARD | PaymentMethod.ACH>,
): formData is CreditCardPaymentFormData => 'name' in formData

/** A confirmation was dispatched, but Tilled did not return a terminal outcome. */
export class PaymentConfirmationOutcomeUnknownError extends Error {
  constructor() {
    super(paymentConfirmationOutcomeUnknownMessage)
    this.name = 'PaymentConfirmationOutcomeUnknownError'
  }
}

export const isPaymentConfirmationOutcomeUnknownError = (
  error: unknown,
): error is PaymentConfirmationOutcomeUnknownError =>
  error instanceof PaymentConfirmationOutcomeUnknownError ||
  (error instanceof Error && error.message === paymentConfirmationOutcomeUnknownMessage)

type UseSubmitTilledPaymentProps<PaymentMethodType extends PaymentMethod> = {
  accountGuid: AccountGuid
  invoice: AbridgedInvoiceMetadata
  paymentAmountUsc: number
  tilledFormInfo?: TilledFormInfo
  links?: {
    jobGuid?: string
    jobAppointmentGuid?: string
    maintenancePlanGuid?: string
  }
  paymentMethod: PaymentMethodType
  /* Hands error handling off to the caller */
  onError: (error: TilledPaymentErrorDetail | Error) => void
  shouldSendReceipt?: boolean
}

const TilledFundingToPaymentMethodCardType: Record<TilledCardFundingType, PaymentMethodCardType> = {
  credit: 'CREDIT',
  debit: 'DEBIT',
  prepaid: 'PREPAID',
  unknown: 'UNKNOWN',
}

export const convertTilledCardInfoToPaymentMethodCardMetadata = (card: CardInfo): PaymentMethodCardMetadata => ({
  lastFourDigits: card.last4,
  expirationYear: `${card.exp_year}`,
  expirationMonth: `${card.exp_month}`.padStart(2, '0'),
  cardBrand: card.brand,
  cardType: card.funding ? TilledFundingToPaymentMethodCardType[card.funding] : 'UNKNOWN',
})

export const getTilledPaymentMethodDetails = <PaymentMethodType extends PaymentMethod>(
  paymentMethod: PaymentMethodType,
  formData: PaymentFormData<PaymentMethod.CARD | PaymentMethod.ACH>,
): TilledPaymentMethodRequest => {
  if (paymentMethod === PaymentMethod.ACH && isAchPaymentFormData(formData)) {
    return {
      type: 'ach_debit',
      billing_details: {
        name: formData.accountHolderName,
        address: {
          zip: `${formData.zipCode}`,
          country: 'US',
          street: formData.streetAddress,
          street2: formData.streetAddress2,
          city: formData.city,
          state: formData.state,
        },
      },
      ach_debit: {
        account_type: formData.accountType,
        account_holder_name: formData.accountHolderName.slice(0, 22),
      },
    }
  }

  if (paymentMethod === PaymentMethod.CARD && isCreditCardFormData(formData)) {
    if (!formData.selectedCardOnFile && !formData.name)
      throw new ThisShouldNeverHappenError('Missing name during payment method creation')
    return {
      type: 'card',
      billing_details: {
        name: formData.name ?? 'Unknown',
        address: {
          zip: `${formData.zipCode}`,
          country: 'US',
          street: formData.streetAddress,
          street2: formData.streetAddress2,
          city: formData.city,
          state: formData.state,
        },
        email: formData.sendEmail ? formData.emailAddress : undefined,
      },
    }
  }

  throw new ThisShouldNeverHappenError('Invalid payment method or Tilled form data.')
}

type SubmitTilledPaymentReferrer = 'self-serve' | 'payment-workflow'

type RecordSubmitPaymentRUMEvent = {
  referrer: SubmitTilledPaymentReferrer
  status: 'success' | 'error'
  paymentMethod: PaymentMethod
  savedNewPaymentMethod?: boolean
  usedExistingPaymentMethod?: boolean
}
const recordSubmitPaymentRUMEvent = (event: RecordSubmitPaymentRUMEvent) => {
  datadogRum.addAction('bz-payment-tilled-submit', event)
}

const isDefinitiveTilledConfirmationRejection = (paymentMethod: PaymentMethod, error: unknown): boolean => {
  if (!isTilledPaymentErrorDetail(error)) return false

  const errorCode = error.code.toUpperCase()
  const documentedCodes: readonly string[] =
    paymentMethod === PaymentMethod.CARD ? TilledCardDeclineErrorCodes : TilledAchDebitReturnErrorCodes
  return documentedCodes.includes(errorCode)
}

export const useSubmitTilledPayment = <PaymentMethodType extends PaymentMethod.CARD | PaymentMethod.ACH>({
  accountGuid,
  tilledFormInfo,
  invoice,
  paymentAmountUsc,
  links,
  paymentMethod,
  onError,
  shouldSendReceipt = true,
}: UseSubmitTilledPaymentProps<PaymentMethodType>) => {
  const message = useMessage()
  const registerIntentMutation = trpc.payments['payments:register-intent'].useMutation()
  const unauthRegisterIntentMutation = trpc.payments['unauth:payments:register-intent'].useMutation()
  const recordPaymentMutation = trpc.payments['payments:record'].useMutation()
  const unauthRecordPaymentMutation = trpc.payments['unauth:payments:record'].useMutation()
  const [isLoading, setIsLoading] = useState(false)
  const [didSucceed, setDidSucceed] = useState(false)
  const {
    createPaymentMethod,
    updatePaymentMethod,
    isLoading: isCreatingPaymentMethod,
  } = useCreateTilledPaymentMethod({
    accountGuid,
    tilledFormInfo,
    paymentMethod,
    onError,
  })

  const getPaymentMethodDetails = useCallback(
    (formData: PaymentFormData<PaymentMethod.CARD | PaymentMethod.ACH>) =>
      getTilledPaymentMethodDetails(paymentMethod, formData),
    [paymentMethod],
  )

  const onSubmit = useCallback(
    async (
      formData: PaymentFormData<PaymentMethod.CARD | PaymentMethod.ACH>,
      companyGuid: string,
      referrer: SubmitTilledPaymentReferrer,
    ) => {
      if (!tilledFormInfo) {
        console.error('Tilled form info undefined')
        message.error('Cannot submit payment. Please try again later.')
        return
      }
      const { tilled } = tilledFormInfo
      if (!tilled) {
        console.error('Tilled client undefined')
        message.error('Cannot submit payment. Please try again later.')
        return
      }

      setIsLoading(true)
      const paymentRecordGuid = nextGuid()
      const paymentMethodRecordGuid: string | undefined = isCreditCardFormData(formData)
        ? formData.selectedCardOnFile?.paymentMethodRecordGuid
        : undefined
      const authenticated = referrer === 'payment-workflow'
      const recordPayment = authenticated ? recordPaymentMutation : unauthRecordPaymentMutation
      const registerIntent = authenticated ? registerIntentMutation : unauthRegisterIntentMutation
      let paymentRecordStarted = false
      let confirmationAttempted = false
      try {
        const tilledPaymentMethodRequest = getPaymentMethodDetails(formData)
        const tilledPaymentMethodId: string | undefined = isCreditCardFormData(formData)
          ? formData.selectedCardOnFile?.externalPaymentMethodId
          : undefined

        await recordPayment.mutateAsync({
          invoiceGuid: invoice.invoiceGuid,
          jobGuid: links?.jobGuid,
          jobAppointmentGuid: links?.jobAppointmentGuid,
          maintenancePlanGuid: links?.maintenancePlanGuid,
          accountGuid,
          companyGuid,
          paymentRecordGuid,
          status: PaymentStatus.SUBMITTING,
          invoiceReferenceNumber: `${invoice.displayId}`,
          amountUsd: usCentsToUsd(paymentAmountUsc),
          paymentMethod,
          errorInfo: null,
          paymentMethodRecordGuid,
          shouldSendReceipt,
        })
        paymentRecordStarted = true

        const { clientSecret, merchantId } = await registerIntent.mutateAsync({
          paymentRecordGuid,
          accountGuid,
          companyGuid,
          invoiceGuid: invoice.invoiceGuid,
          amountUsd: usCentsToUsd(paymentAmountUsc),
          jobGuid: links?.jobGuid,
          jobAppointmentGuid: links?.jobAppointmentGuid,
          paymentMethodRecordGuid,
        })
        if (merchantId !== tilledFormInfo.accountId) {
          throw new Error('The selected card account changed. Reselect the card and try again.')
        }

        const address = isCreditCardFormData(formData)
          ? formData.selectedCardOnFile?.paymentMethodBillingInfo?.address
          : undefined

        if (
          tilledPaymentMethodId &&
          paymentMethodRecordGuid &&
          address &&
          (formData.streetAddress !== address.line1 ||
            formData.streetAddress2 !== address.line2 ||
            formData.city !== address.city ||
            formData.state !== address.stateAbbreviation ||
            formData.zipCode !== address.zipCode)
        ) {
          try {
            await updatePaymentMethod(paymentMethodRecordGuid, formData, companyGuid, tilledPaymentMethodId, false)
          } catch (error) {
            console.error('Payment will continue without updating the saved-card billing address', error)
          }
        }

        confirmationAttempted = true
        const payment = await tilled.confirmPayment(clientSecret, {
          payment_method: tilledPaymentMethodId ?? tilledPaymentMethodRequest,
        })

        const status =
          payment.status === 'succeeded'
            ? PaymentStatus.PAID
            : payment.status === 'processing' ||
                payment.status === 'requires_action' ||
                payment.status === 'requires_capture' ||
                payment.status === 'requires_confirmation'
              ? PaymentStatus.PENDING
              : payment.status === 'canceled'
                ? PaymentStatus.CANCELED
                : PaymentStatus.FAILED

        await recordPayment.mutateAsync({
          invoiceGuid: invoice.invoiceGuid,
          accountGuid,
          companyGuid,
          paymentRecordGuid,
          status,
          statusDetail: payment.last_payment_error?.message ?? payment.status,
          invoiceReferenceNumber: `${invoice.displayId}`,
          occurredAt: payment.created_at,
          amountUsd: usCentsToUsd(payment.amount),
          externalPaymentId: payment.id,
          paymentMethod,
          jobGuid: links?.jobGuid,
          jobAppointmentGuid: links?.jobAppointmentGuid,
          maintenancePlanGuid: links?.maintenancePlanGuid,
          paymentMethodRecordGuid,
          errorInfo: payment.last_payment_error
            ? (getTilledPaymentErrorDetail(paymentMethod, payment.last_payment_error) ?? null)
            : null,
        })

        if (status === PaymentStatus.PENDING) {
          recordSubmitPaymentRUMEvent({
            referrer,
            status: 'error',
            paymentMethod,
            savedNewPaymentMethod: false,
            usedExistingPaymentMethod: !!tilledPaymentMethodId,
          })
          onError?.(new PaymentConfirmationOutcomeUnknownError())
        } else if (payment.last_payment_error) {
          onError?.(payment.last_payment_error)
          recordSubmitPaymentRUMEvent({
            referrer,
            status: 'error',
            paymentMethod,
            savedNewPaymentMethod: false,
            usedExistingPaymentMethod: !!tilledPaymentMethodId,
          })
        } else if (status === PaymentStatus.PAID) {
          // Save the payment method _only_ after the payment is successfully recorded. This will prevent us from
          // optimistically saving the payment method and then having the payment fail without having a way to
          // let the user manage their card on file. Also don't try and save the payment method if it's already
          // on file.
          let savedNewPaymentMethod = false
          if (!tilledPaymentMethodId && formData.savePaymentMethod) {
            try {
              await createPaymentMethod(formData, companyGuid, true, authenticated, false)
              savedNewPaymentMethod = true
            } catch (error) {
              console.error('Payment succeeded, but the payment method could not be saved', error)
              message.error('Payment succeeded, but the payment method could not be saved. Add it again later.')
            }
          }
          recordSubmitPaymentRUMEvent({
            referrer,
            status: 'success',
            paymentMethod,
            savedNewPaymentMethod,
            usedExistingPaymentMethod: !!tilledPaymentMethodId,
          })

          setDidSucceed(true)
        } else {
          recordSubmitPaymentRUMEvent({
            referrer,
            status: 'error',
            paymentMethod,
            savedNewPaymentMethod: false,
            usedExistingPaymentMethod: !!tilledPaymentMethodId,
          })
          onError?.(new Error(`Tilled payment confirmation did not succeed: ${payment.status}`))
        }
      } catch (err: unknown) {
        console.error(err)
        const definitiveConfirmationRejection =
          confirmationAttempted && isDefinitiveTilledConfirmationRejection(paymentMethod, err)
        if (paymentRecordStarted && (!confirmationAttempted || definitiveConfirmationRejection)) {
          try {
            await recordPayment.mutateAsync({
              invoiceGuid: invoice.invoiceGuid,
              jobGuid: links?.jobGuid,
              jobAppointmentGuid: links?.jobAppointmentGuid,
              maintenancePlanGuid: links?.maintenancePlanGuid,
              accountGuid,
              companyGuid,
              paymentRecordGuid,
              status: PaymentStatus.FAILED,
              statusDetail: isTilledPaymentErrorDetail(err)
                ? err.message
                : err instanceof Error
                  ? err.message
                  : 'Payment submission failed',
              occurredAt: new Date().toISOString(),
              invoiceReferenceNumber: `${invoice.displayId}`,
              amountUsd: usCentsToUsd(paymentAmountUsc),
              paymentMethod,
              errorInfo: getTilledPaymentErrorDetail(paymentMethod, err) ?? null,
              paymentMethodRecordGuid,
              shouldSendReceipt: false,
            })
          } catch (recordError) {
            console.error('Failed to record payment submission failure', recordError)
          }
        }
        onError(
          confirmationAttempted && !definitiveConfirmationRejection
            ? new PaymentConfirmationOutcomeUnknownError()
            : isTilledPaymentErrorDetail(err)
              ? err
              : (err as Error),
        )
      } finally {
        setIsLoading(false)
      }

      return paymentRecordGuid
    },
    [
      tilledFormInfo,
      message,
      getPaymentMethodDetails,
      recordPaymentMutation,
      unauthRecordPaymentMutation,
      invoice.invoiceGuid,
      invoice.displayId,
      links?.jobGuid,
      links?.jobAppointmentGuid,
      links?.maintenancePlanGuid,
      updatePaymentMethod,
      accountGuid,
      paymentAmountUsc,
      paymentMethod,
      registerIntentMutation,
      unauthRegisterIntentMutation,
      onError,
      createPaymentMethod,
      shouldSendReceipt,
    ],
  )

  return {
    onSubmit,
    isLoading: isLoading || isCreatingPaymentMethod,
    didSucceed,
  }
}
